import dayjs from "dayjs"
import CardSymphony from "elements/CardSymphony"
import Icon from "elements/Icon"
import InputSymphony from "elements/InputSymphony"
import Loader from "elements/Loader"
import ModalSymphony from "elements/ModalSymphony"
import TagSymphony from "elements/TagSymphony"
import useMaestroInstagramPosts from "Hooks/useMaestroInstagramPosts"
import useScreen from "Hooks/useScreen"
import Colors from "modules/Colors"
import Sty from "modules/Sty"
import { clsx, makeKey, pluralize, styx } from "modules/Utils"
import { CSSProperties, useState } from "react"

interface MaestroInstagramAnalyzerProps {
    className?: string
}

// NOTE: component()
const MaestroInstagramAnalyzer = ({
    className,
}: MaestroInstagramAnalyzerProps) => {
    const {
        cssCalc
    } = useScreen();
    const {
        isLoadingMaestroInstagramPosts,
        isShowMaestroInstagramInputModal,
        isShowMaestroInstagramResultsModal,
        setIsShowMaestroInstagramInputModal,
        setIsShowMaestroInstagramResultsModal,
        igUsernameOrUrl,
        setIgUsernameOrUrl,
        postAnalyzeInstagramPosts,
        maestroInstagramPostsData,
    } = useMaestroInstagramPosts()

    const {
        profileData,
        analyzedPosts,
        recommendations
    } = maestroInstagramPostsData || {}

    const {
        followers = 0,
        postsCount = 0
    } = profileData || {}

    const [postViewStates, setPostViewStates] = useState<boolean[]>([])

    const handleStartInstagramAnalysis = async () => {
        try {
            await postAnalyzeInstagramPosts(igUsernameOrUrl)
            setIsShowMaestroInstagramInputModal(false)
            setIsShowMaestroInstagramResultsModal(true)
        } catch (error) {
            console.error(error)
        }
    }

    const toggleViewPost = (index: number) => {
        const newPostViewStates = [...postViewStates]
        newPostViewStates[index] = !newPostViewStates[index]
        setPostViewStates(newPostViewStates)
    }

    const renderRecommendationsLeftSideContent = () => {
        if (!maestroInstagramPostsData) {
            return null
        }

        if (!recommendations) {
            return null
        }

        const {
            contentThemes,
            visualElements,
            audioElements,
            contentPatterns,
            recommendations: recommendationsInner,
            contentIdeas,
            audienceInsights,
        } = recommendations

        return (
            <div
                className="maestroInstagramResultsLeftSideContentContainer"
                style={styx(
                    sty.maestroInstagramResultsLeftSideContentContainer,
                    { height: cssCalc(0) }
                )}
            >
                <CardSymphony
                    className="resultsContentThemes"
                    title="Content Themes"
                    subtitle="This describes the themes of the content of the analyzed posts."
                    size="small"
                    isNoPadding
                >
                    <div style={sty.maestroInfoContainer}>
                        {contentThemes.map((contentTheme: string, index: number) => {
                            return (
                                <TagSymphony
                                    key={makeKey('maestro-instagram-analyzer-content-theme', index)}
                                    className="maestroInstagramResultsPostContentTheme"
                                    title={contentTheme}
                                />
                            )
                        })}
                    </div>
                </CardSymphony>

                <CardSymphony
                    className="resultsVisualElements"
                    title="Visual Elements"
                    subtitle="This describes the visual elements of the content of the analyzed posts."
                    size="small"
                    isNoPadding
                >
                    <div style={sty.maestroInfoContainer}>
                        {visualElements.map((visualElement: string, index: number) => {
                            return (
                                <TagSymphony
                                    key={makeKey('maestro-instagram-analyzer-visual-element', index)}
                                    className="maestroInstagramResultsPostVisualElement"
                                    title={visualElement}
                                />
                            )
                        })}
                    </div>
                </CardSymphony>

                <CardSymphony
                    className="resultsAudioElements"
                    title="Audio Elements"
                    subtitle="This describes the audio elements of the content of the analyzed posts."
                    size="small"
                    isNoPadding
                >
                    <div style={sty.maestroInfoContainer}>
                        {audioElements.map((audioElement: string, index: number) => {
                            return (
                                <TagSymphony
                                    key={makeKey('maestro-instagram-analyzer-audio-element', index)}
                                    className="maestroInstagramResultsPostAudioElement"
                                    title={audioElement}
                                />
                            )
                        })}
                    </div>
                </CardSymphony>

                <CardSymphony
                    className="resultsContentPatterns"
                    title="Content Patterns"
                    subtitle="This describes the content patterns of the content of the analyzed posts."
                    size="small"
                    isNoPadding
                >
                    <div style={sty.maestroInfoContainer}>
                        {contentPatterns.map((contentPattern: string, index: number) => {
                            return (
                                <TagSymphony
                                    key={makeKey('maestro-instagram-analyzer-content-pattern', index)}
                                    className="maestroInstagramResultsPostContentPattern"
                                    title={contentPattern}
                                />
                            )
                        })}
                    </div>
                </CardSymphony>

                <CardSymphony
                    className="resultsRecommendationsInner"
                    title="Recommendations Inner"
                    subtitle="This describes the recommendations inner of the content of the analyzed posts."
                    size="small"
                    isNoPadding
                >
                    <div style={sty.maestroInfoContainer}>
                        {recommendationsInner.map((recommendationInner: string, index: number) => {
                            return (
                                <TagSymphony
                                    key={makeKey('maestro-instagram-analyzer-recommendation-inner', index)}
                                    className="maestroInstagramResultsPostRecommendationInner"
                                    title={recommendationInner}
                                />
                            )
                        })}
                    </div>
                </CardSymphony>

                <CardSymphony
                    className="resultsContentIdeas"
                    title="Content Ideas"
                    subtitle="This describes the content ideas of the content of the analyzed posts."
                    size="small"
                    isNoPadding
                >
                    <div style={sty.maestroInfoContainer}>
                        {contentIdeas.map((contentIdea: string, index: number) => {
                            return (
                                <TagSymphony
                                    key={makeKey('maestro-instagram-analyzer-content-idea', index)}
                                    className="maestroInstagramResultsPostContentIdea"
                                    title={contentIdea}
                                />
                            )
                        })}
                    </div>
                </CardSymphony>

                <CardSymphony
                    className="resultsAudienceInsights"
                    title="Audience Insights"
                    subtitle="This describes the audience insights of the content of the analyzed posts."
                    size="small"
                    isNoPadding
                >
                    <div style={{ fontStyle: 'italic' }}>
                        Demographics
                    </div>
                    <div style={sty.maestroInfoContainer}>
                        {audienceInsights.demographics.map((demographic: string, index: number) => {
                            return (
                                <TagSymphony
                                    key={makeKey('maestro-instagram-analyzer-demographic', index)}
                                    className="maestroInstagramResultsPostDemographic"
                                    title={demographic}
                                />
                            )
                        })}
                    </div>

                    <div style={{ fontStyle: 'italic' }}>
                        Interests
                    </div>
                    <div style={sty.maestroInfoContainer}>
                        {audienceInsights.interests.map((interest: string, index: number) => {
                            return (
                                <TagSymphony
                                    key={makeKey('maestro-instagram-analyzer-interest', index)}
                                    className="maestroInstagramResultsPostInterest"
                                    title={interest}
                                />
                            )
                        })}
                    </div>

                    <div style={{ fontStyle: 'italic' }}>
                        Behaviors
                    </div>
                    <div style={sty.maestroInfoContainer}>
                        {audienceInsights.behaviors.map((behavior: string, index: number) => {
                            return (
                                <TagSymphony
                                    key={makeKey('maestro-instagram-analyzer-behavior', index)}
                                    className="maestroInstagramResultsPostBehavior"
                                    title={behavior}
                                />
                            )
                        })}
                    </div>
                </CardSymphony>
            </div>
        )
    }

    const renderMaestroInstagramResults = () => {
        if (!maestroInstagramPostsData) {
            return (
                <div className="maestroInstagramResultsNoData">
                    No data found
                </div>
            )
        }

        if (isLoadingMaestroInstagramPosts) {
            return (
                <Loader title="Analyzing your Instagram profile..." isCentered />
            )
        }

        if (!analyzedPosts) {
            return (
                <div className="maestroInstagramResultsNoData">
                    No data found
                </div>
            )
        }

        return (
            <div className="maestroInstagramResultsContainer" style={sty.maestroInstagramResultsContainer}>
                {analyzedPosts?.slice(0, 5).map((analyzedPost: any, index: number) => {
                    const {
                        postMetadata,
                        analysis
                    } = analyzedPost

                    if (!postMetadata || !analysis) {
                        return null
                    }

                    const {
                        content_breakdown,
                        target_audience_analysis
                    } = analysis

                    return (
                        <div
                            key={makeKey('maestro-instagram-analyzer-post', index)}
                            className="maestroInstagramResultsPost"
                            style={styx(
                                sty.maestroInstagramResultsPost,
                                { background: Colors.GRADIENT_INSTAGRAM }
                            )}
                        >
                            <div
                                className="maestroInstagramResultsPostTitle"
                                style={{
                                    ...Sty.text24bold,
                                    color: Colors.purple,
                                    cursor: 'pointer',
                                }}
                                onClick={() => toggleViewPost(index)}
                            >
                                Instagram Post {index + 1}
                            </div>
                            {postViewStates[index] &&
                                <>
                                    <div
                                        className="maestroInstagramResultsPostDate"
                                        style={{
                                            ...Sty.text14grey,
                                            color: Colors.purple
                                        }}
                                    >
                                        {dayjs(postMetadata.timestamp).format('DD/MM/YYYY')}
                                    </div>
                                    <div className="maestroInstagramResultsPostCaption">
                                        {postMetadata.username}: {postMetadata.caption}
                                    </div>
                                    <CardSymphony
                                        className="detailedSummary"
                                        title="Detailed Summary"
                                        subtitle={analysis.detailed_summary}
                                        size="small"
                                        isNoPadding
                                        style={sty.cardSymphonyMaestroInstagramAnalyzer}
                                    />
                                    <CardSymphony
                                        className="analysisContentBreakdown"
                                        title="Content Breakdown"
                                        subtitle="This will help you understand the content of the post and how it is structured."
                                        size="small"
                                        isNoPadding
                                        style={sty.cardSymphonyMaestroInstagramAnalyzer}
                                    >
                                        <div>
                                            Video Narrative
                                        </div>
                                        <div style={Sty.text14grey}>
                                            {content_breakdown.video_narrative}
                                        </div>

                                        <div>
                                            Visual Elements
                                        </div>
                                        <div style={sty.maestroInfoContainer}>
                                            {content_breakdown.visual_elements.map((visualElement: string, index: number) => {
                                                return (
                                                    <TagSymphony
                                                        key={makeKey('maestro-instagram-analyzer-visual-element', index)}
                                                        className="maestroInstagramResultsPostVisualElement"
                                                        title={visualElement}
                                                    />
                                                )
                                            })}
                                        </div>

                                        <div>
                                            Themes & Topics
                                        </div>
                                        <div style={sty.maestroInfoContainer}>
                                            {content_breakdown.themes_topics.map((theme: string, index: number) => {
                                                return (
                                                    <TagSymphony
                                                        key={makeKey('maestro-instagram-analyzer-theme', index)}
                                                        className="maestroInstagramResultsPostTheme"
                                                        title={theme}
                                                    />
                                                )
                                            })}
                                        </div>
                                    </CardSymphony>
                                    <CardSymphony
                                        className="analysisTargetAudienceAnalysis"
                                        title="Target Audience Analysis"
                                        size="small"
                                        isNoPadding
                                        style={sty.cardSymphonyMaestroInstagramAnalyzer}
                                    >
                                        <div>
                                            Demographics
                                        </div>
                                        <div style={sty.maestroInfoContainer}>
                                            {target_audience_analysis.demographics.map((demographic: string, index: number) => {
                                                return (
                                                    <TagSymphony
                                                        key={makeKey('maestro-instagram-analyzer-demographic', index)}
                                                        className="maestroInstagramResultsPostDemographic"
                                                        title={demographic}
                                                    />
                                                )
                                            })}
                                        </div>

                                        <div>
                                            Psychographics
                                        </div>
                                        <div style={sty.maestroInfoContainer}>
                                            {target_audience_analysis.psychographics.map((psychographic: string, index: number) => {
                                                return (
                                                    <TagSymphony
                                                        key={makeKey('maestro-instagram-analyzer-psychographic', index)}
                                                        className="maestroInstagramResultsPostPsychographic"
                                                        title={psychographic}
                                                    />
                                                )
                                            })}
                                        </div>

                                        <div>
                                            Public Figures & Brands
                                        </div>
                                        <div style={sty.maestroInfoContainer}>
                                            {target_audience_analysis.public_figures_brands.map((publicFigureOrBrand: string, index: number) => {
                                                return (
                                                    <TagSymphony
                                                        key={makeKey('maestro-instagram-analyzer-public-figure-or-brand', index)}
                                                        className="maestroInstagramResultsPostPublicFigureOrBrand"
                                                        title={publicFigureOrBrand}
                                                    />
                                                )
                                            })}
                                        </div>

                                        <div>
                                            Topics
                                        </div>
                                        <div style={sty.maestroInfoContainer}>
                                            {target_audience_analysis.topics.map((topic: string, index: number) => {
                                                return (
                                                    <TagSymphony
                                                        key={makeKey('maestro-instagram-analyzer-topic', index)}
                                                        className="maestroInstagramResultsPostTopic"
                                                        title={topic}
                                                    />
                                                )
                                            })}
                                        </div>
                                    </CardSymphony>
                                </>
                            }
                        </div>
                    )
                })}
            </div>
        )
    }

    // NOTE: render()
    return (
        <>
            <ModalSymphony
                className={clsx(
                    'maestroInstagramInputModal',
                    className
                )}
                isOpen={isShowMaestroInstagramInputModal}
                onClose={() => setIsShowMaestroInstagramInputModal(false)}
                title="Your Marketing Report"
                subtitle="Enter your Instagram profile to get a content analysis report."
                onClickButtonRight={handleStartInstagramAnalysis}
                buttonRightText="Start"
                style={{ gap: 16 }}
            >
                <div className="maestroInstagramInputContent" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon
                        className="maestroInstagramInputIcon"
                        icon="maestroBadgeImage"
                    />
                    <div className="maestroInstagramInputContentText">
                        Content Plan Suggested by AI
                    </div>
                </div>
                <InputSymphony
                    className="maestroInstagramInput"
                    value={igUsernameOrUrl}
                    onChange={value => {
                        setIgUsernameOrUrl(value)
                    }}
                    placeholder="username or https://www.instagram.com/username"
                    label="Instagram Username or URL"
                />
            </ModalSymphony>

            <ModalSymphony
                className={clsx(
                    'maestroInstagramResultsModal',
                    className
                )}
                isOpen={isShowMaestroInstagramResultsModal}
                onClose={() => {
                    setIsShowMaestroInstagramResultsModal(false)
                    setIgUsernameOrUrl('')
                }}
                title="Maestro Instagram Analyzer"
                subtitle={`Analyzed ${postsCount} ${pluralize('post', postsCount)}`}
                leftSideContent={renderRecommendationsLeftSideContent()}
                styleModalContent={{ borderLeft: Sty.border, flex: 1 }}
                styleLeftSideContent={{ flex: 1 }}
                maxWidth={1120}
            >
                {renderMaestroInstagramResults()}
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    maestroInfoContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    maestroInstagramResultsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
    maestroInstagramResultsPost: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 16,
        borderRadius: 16,
        backgroundColor: Colors.greyLight,
    },
    cardSymphonyMaestroInstagramAnalyzer: {
        backgroundColor: 'transparent',
    },
    maestroInstagramResultsLeftSideContentContainer: {
        position: 'sticky',
        top: -24,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        borderRadius: 16,
        padding: 16,
        overflowY: 'scroll',
    },
}

export default MaestroInstagramAnalyzer